import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getKbRegions } from '../../lib/api/kb-api';
import './KBDataPage.css';

const INDEX_TYPES = [
  { value: 'SALE', label: '매매증감' },
  { value: 'JEONSE', label: '전세증감' },
  { value: 'SALE_IDX', label: '매매지수' },
  { value: 'JEONSE_IDX', label: '전세지수' },
  { value: 'BUYER_SELLER', label: '매수매도심리' }
];

const TOP_REGION_HEADER_NAMES = new Set([
  '전국', '서울', '강북14개구', '강남11개구', '수도권', '6개광역시', '5개광역시', '기타지방',
  '부산', '대구', '인천', '광주', '대전', '울산', '세종', '경기', '충북', '충남', '전남', '경북', '경남', '제주', '강원', '전북',
  '서울특별시', '부산광역시', '대구광역시', '인천광역시', '광주광역시', '대전광역시', '울산광역시',
  '세종특별자치시', '경기도', '충청북도', '충청남도', '전라남도', '경상북도', '경상남도', '제주특별자치도',
  '강원특별자치도', '전북특별자치도',
  '(구)광주광역시', '(구)전라남도'
]);

const DEFAULT_REGION_OPTIONS = [
  { regionId: 735, regionNameKo: '전국' },
  { regionId: 736, regionNameKo: '서울' },
  { regionId: 737, regionNameKo: '강북14개구' },
  { regionId: 738, regionNameKo: '강남11개구' },
  { regionId: 739, regionNameKo: '수도권' },
  { regionId: 740, regionNameKo: '6개광역시' },
  { regionId: 741, regionNameKo: '5개광역시' },
  { regionId: 742, regionNameKo: '기타지방' },
  { regionId: 743, regionNameKo: '부산' },
  { regionId: 745, regionNameKo: '대구' },
  { regionId: 746, regionNameKo: '인천' },
  { regionId: 747, regionNameKo: '(구)광주광역시' },
  { regionId: 748, regionNameKo: '대전' },
  { regionId: 749, regionNameKo: '울산' },
  { regionId: 750, regionNameKo: '세종' },
  { regionId: 751, regionNameKo: '경기' },
  { regionId: 752, regionNameKo: '충북' },
  { regionId: 753, regionNameKo: '충남' },
  { regionId: 754, regionNameKo: '(구)전라남도' },
  { regionId: 755, regionNameKo: '경북' },
  { regionId: 756, regionNameKo: '경남' },
  { regionId: 757, regionNameKo: '제주' },
  { regionId: 758, regionNameKo: '강원' },
  { regionId: 759, regionNameKo: '전북' },
];

const KBDataPage = () => {
  const [searchParams] = useSearchParams();
  const [indexType, setIndexType] = useState('SALE_IDX');
  const [dateSortOrder, setDateSortOrder] = useState('DESC');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [regionOptions, setRegionOptions] = useState(DEFAULT_REGION_OPTIONS);
  const [selectedRegionIds, setSelectedRegionIds] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const queryKey = useMemo(() => searchParams.toString(), [searchParams]);
  const lastAppliedQueryRef = useRef('');

  const fetchData = useCallback(async (override = {}) => {
    const effectiveIndexType = override.indexType || indexType;
    const effectiveDateRange = override.dateRange || dateRange;
    const effectiveRegionIds = Array.isArray(override.regionIds) ? override.regionIds : selectedRegionIds;
    const effectiveSortOrder = override.sortOrder || dateSortOrder;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (effectiveDateRange.startDate) params.append('startDate', effectiveDateRange.startDate);
      if (effectiveDateRange.endDate) params.append('endDate', effectiveDateRange.endDate);
      if (effectiveRegionIds.length > 0) {
        params.append('regionIds', effectiveRegionIds.join(','));
      }

      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7133';

      let endpoint;
      if (effectiveIndexType === 'BUYER_SELLER') {
        endpoint = `/api/kb/pivot/buyer-sentiment?${params}`;
      } else {
        params.append('indexType', effectiveIndexType);
        params.append('sortOrder', effectiveSortOrder);
        endpoint = `/api/kb/pivot/table?${params}`;
      }

      const response = await fetch(`${apiUrl}${endpoint}`);

      if (!response.ok) {
        throw new Error('데이터 조회에 실패했습니다.');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching KB data:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, indexType, selectedRegionIds, dateSortOrder]);

  const handleSearch = () => {
    fetchData();
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    const loadRegions = async () => {
      try {
        const result = await getKbRegions();
        const items = Array.isArray(result?.items) ? result.items : (Array.isArray(result) ? result : []);
        if (items.length > 0) {
          setRegionOptions(items.map((item) => ({
            regionId: Number(item.regionId ?? item.region_id),
            regionNameKo: item.regionNameKo ?? item.region_name_ko,
          })));
        }
      } catch {
        // fallback: DEFAULT_REGION_OPTIONS 유지
      }
    };

    loadRegions();
  }, []);

  useEffect(() => {
    if (lastAppliedQueryRef.current === queryKey) {
      return;
    }
    lastAppliedQueryRef.current = queryKey;

    const qsIndexType = searchParams.get('indexType');
    const qsStartDate = searchParams.get('startDate') || '';
    const qsEndDate = searchParams.get('endDate') || '';
    const qsRegionIds = searchParams.get('regionIds') || '';
    const autoSearch = searchParams.get('autoSearch') === '1';

    const allowedIndexTypes = new Set(INDEX_TYPES.map((item) => item.value));
    const nextIndexType = allowedIndexTypes.has(String(qsIndexType)) ? qsIndexType : null;

    if (nextIndexType) {
      setIndexType(nextIndexType);
    }

    if (qsStartDate || qsEndDate) {
      setDateRange({
        startDate: qsStartDate,
        endDate: qsEndDate,
      });
    }

    const parsedRegionIds = qsRegionIds
      .split(',')
      .map((item) => Number(item.trim()))
      .filter((item) => Number.isFinite(item) && item > 0);

    if (parsedRegionIds.length > 0) {
      setSelectedRegionIds(Array.from(new Set(parsedRegionIds)));
    }

    if (autoSearch && (nextIndexType || qsStartDate || qsEndDate || parsedRegionIds.length > 0)) {
      fetchData({
        indexType: nextIndexType || undefined,
        dateRange: {
          startDate: qsStartDate,
          endDate: qsEndDate,
        },
        regionIds: parsedRegionIds,
      });
    }
  }, [fetchData, queryKey, searchParams]);

  // 정렬 순서 변경 시 자동으로 데이터 다시 가져오기
  useEffect(() => {
    if (data) {
      fetchData();
    }
  }, [dateSortOrder]);

  const selectAllRegions = () => {
    setSelectedRegionIds(regionOptions.map((region) => region.regionId));
  };

  const deselectAllRegions = () => {
    setSelectedRegionIds([]);
  };

  const toggleRegion = (regionId) => {
    setSelectedRegionIds((prev) => {
      if (prev.includes(regionId)) {
        return prev.filter((id) => id !== regionId);
      }
      return [...prev, regionId];
    });
  };

  const getRegionHierarchy = (headers) => {
    if (!headers || headers.length === 0) return [];

    const hierarchy = [];
    let currentParent = null;
    let currentChildren = [];

    headers.forEach((header) => {
      if (TOP_REGION_HEADER_NAMES.has(String(header || '').trim())) {
        if (currentParent) {
          hierarchy.push({ parent: currentParent, children: currentChildren });
        }
        currentParent = header;
        currentChildren = [];
      } else {
        currentChildren.push(header);
      }
    });

    if (currentParent) {
      hierarchy.push({ parent: currentParent, children: currentChildren });
    }

    return hierarchy;
  };

  const openTableInNewWindow = () => {
    if (!data) return;

    const newWindow = window.open('', '_blank', 'width=1400,height=800,scrollbars=yes,resizable=yes');
    if (!newWindow) {
      alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
      return;
    }

    const headers = Array.isArray(data.headers) ? data.headers : [];
    const regionHierarchy = getRegionHierarchy(headers);
    const subHeaders = Array.isArray(data.subHeaders) ? data.subHeaders : null;
    const rows = Array.isArray(data.data) ? data.data : [];
    const isWeeklyChangeTable = indexType === 'SALE' || indexType === 'JEONSE';
    const parentRegionSet = new Set(regionHierarchy.map((g) => g.parent));

    const getWeeklyChangeClassByDelta = (delta) => {
      if (!isWeeklyChangeTable || delta === null || delta === undefined) {
        return '';
      }

      const num = Number(delta);
      if (Number.isNaN(num)) {
        return '';
      }

      const abs = Math.abs(num);
      if (num >= 0.8) return 'cell-rise-4';
      if (num >= 0.6) return 'cell-rise-3';
      if (num >= 0.4) return 'cell-rise-2';
      if (num >= 0.2) return 'cell-rise-1';

      if (num <= -0.8) return 'cell-fall-4';
      if (num <= -0.6) return 'cell-fall-3';
      if (num <= -0.4) return 'cell-fall-2';
      if (num <= -0.2) return 'cell-fall-1';

      if (abs < 0.2) return 'cell-neutral';
      return '';
    };

    let tableHtml = '';

    if (subHeaders && subHeaders.length > 0) {
      tableHtml = `
        <table class="kb-table buyer-sentiment-table">
          <thead>
            <tr>
              <th class="sticky-col" rowspan="2">날짜</th>
              ${headers.map((region) => `<th colspan="${subHeaders.length}">${region}</th>`).join('')}
            </tr>
            <tr>
              ${headers.map(() =>
                subHeaders.map((subHeader) => `<th class="sub-header">${subHeader}</th>`).join('')
              ).join('')}
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => `
              <tr>
                <td class="sticky-col">${row.baseDate || '-'}</td>
                ${headers.map((region) =>
                  subHeaders.map((subHeader) => {
                    const key = subHeader === '매도자많음' ? 'seller'
                      : subHeader === '매수자많음' ? 'buyer'
                        : 'index';
                    const value = row.values?.[key]?.[region];
                    const prevWeekIndex = dateSortOrder === 'DESC' ? rows.indexOf(row) + 1 : rows.indexOf(row) - 1;
                    const prevValue = (prevWeekIndex >= 0 && prevWeekIndex < rows.length)
                      ? rows[prevWeekIndex]?.values?.[key]?.[region]
                      : null;
                    const delta =
                      value !== null && value !== undefined && prevValue !== null && prevValue !== undefined
                        ? Number(value) - Number(prevValue)
                        : null;
                    const className = getWeeklyChangeClassByDelta(delta);
                    return `<td class="${className}">${value !== null && value !== undefined ? value.toFixed(2) : '-'}</td>`;
                  }).join('')
                ).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    } else {
      const guideHtml = isWeeklyChangeTable
        ? '<div class="kb-change-guide">전 주 데이터 대비 음영 기준: ±0.20%, ±0.40%, ±0.60%, ±0.80% 이상 구간별 상승/하락 표시</div>'
        : '';

      tableHtml = `
        ${guideHtml}
        <table class="kb-table kb-table-2row">
          <thead>
            <tr>
              <th class="sticky-col" rowspan="2">날짜</th>
              ${headers.map((region) => {
                const isParent = parentRegionSet.has(region);
                return `<th class="${isParent ? 'parent-region-header' : 'child-region-header empty-cell'}">${isParent ? region : ''}</th>`;
              }).join('')}
            </tr>
            <tr>
              ${headers.map((region) => {
                const isParent = parentRegionSet.has(region);
                return `<th class="${isParent ? 'child-region-header empty-cell' : 'child-region-header'}">${isParent ? '' : region}</th>`;
              }).join('')}
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => `
              <tr>
                <td class="sticky-col">${row.baseDate || '-'}</td>
                ${headers.map((region) => {
                  const value = row.values?.[region];
                  const prevWeekIndex = dateSortOrder === 'DESC' ? rows.indexOf(row) + 1 : rows.indexOf(row) - 1;
                  const prevValue = (prevWeekIndex >= 0 && prevWeekIndex < rows.length)
                    ? rows[prevWeekIndex]?.values?.[region]
                    : null;
                  const delta =
                    value !== null && value !== undefined && prevValue !== null && prevValue !== undefined
                      ? Number(value) - Number(prevValue)
                      : null;
                  const className = getWeeklyChangeClassByDelta(delta);
                  return `<td class="${className}">${value !== null && value !== undefined ? value.toFixed(2) : '-'}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    }

    const cssLink = window.location.origin + '/static/css/kb-popup.css';

    newWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>KB 데이터 - ${INDEX_TYPES.find(t => t.value === indexType)?.label || indexType}</title>
        <link rel="stylesheet" href="${cssLink}">
        <style>
          body {
            margin: 0;
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            background-color: #f5f5f5;
          }
          .kb-change-guide {
            margin-bottom: 10px;
            padding: 8px 12px;
            background-color: #f0f8ff;
            border-left: 4px solid #4a90e2;
            font-size: 13px;
            color: #333;
          }
          .kb-table-container {
            overflow-x: auto;
            overflow-y: auto;
            background: white;
            border-radius: 4px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            max-width: 100%;
          }
          .kb-table {
            border-collapse: collapse;
            font-size: 13px;
            min-width: max-content;
          }
          .kb-table th,
          .kb-table td {
            padding: 8px 12px;
            border: 1px solid #ddd;
            text-align: center;
          }
          .kb-table th {
            background-color: #f8f9fa;
            font-weight: 600;
            white-space: nowrap;
          }
          .kb-table .sticky-col {
            position: sticky;
            left: 0;
            background-color: #fff;
            z-index: 10;
            font-weight: 500;
          }
          .kb-table thead th.sticky-col {
            background-color: #f8f9fa;
            z-index: 11;
          }
          .parent-region-header {
            background-color: #e3f2fd !important;
            font-weight: 700;
          }
          .child-region-header.empty-cell {
            background-color: #f8f9fa !important;
          }
          .sub-header {
            background-color: #f1f3f5 !important;
            font-size: 12px;
          }
          .cell-rise-1 { background-color: #b4ddc6; }
          .cell-rise-2 { background-color: #3ed07c; }
          .cell-rise-3 { background-color: #19a0ff; }
          .cell-rise-4 { background-color: #0d767f; color: #ffffff; }
          .cell-fall-1 { background-color: #f2d2c9; }
          .cell-fall-2 { background-color: #e6a4a4; }
          .cell-fall-3 { background-color: #d76d67; }
          .cell-fall-4 { background-color: #d5534b; color: #ffffff; }
          .cell-neutral { background-color: #f9f9f9; }
        </style>
      </head>
      <body>
        <div class="kb-table-container">
          ${tableHtml}
        </div>
      </body>
      </html>
    `);

    newWindow.document.close();
  };

  const renderTable = () => {
    if (!data) {
      return <div className="no-data">검색 버튼을 눌러 데이터를 조회하세요.</div>;
    }

    if (!data.data || data.data.length === 0) {
      return <div className="no-data">데이터가 없습니다.</div>;
    }

    const headers = Array.isArray(data.headers) ? data.headers : [];

    const regionHierarchy = getRegionHierarchy(headers);
    const subHeaders = Array.isArray(data.subHeaders) ? data.subHeaders : null;
    const rows = Array.isArray(data.data) ? data.data : [];
    const isWeeklyChangeTable = indexType === 'SALE' || indexType === 'JEONSE';

    const getWeeklyChangeClassByDelta = (delta) => {
      if (!isWeeklyChangeTable || delta === null || delta === undefined) {
        return '';
      }

      const num = Number(delta);
      if (Number.isNaN(num)) {
        return '';
      }

      const abs = Math.abs(num);
      if (num >= 0.8) return 'cell-rise-4';
      if (num >= 0.6) return 'cell-rise-3';
      if (num >= 0.4) return 'cell-rise-2';
      if (num >= 0.2) return 'cell-rise-1';

      if (num <= -0.8) return 'cell-fall-4';
      if (num <= -0.6) return 'cell-fall-3';
      if (num <= -0.4) return 'cell-fall-2';
      if (num <= -0.2) return 'cell-fall-1';

      if (abs < 0.2) return 'cell-neutral';
      return '';
    };

    if (headers.length === 0) {
      return <div className="no-data">헤더 정보가 없습니다.</div>;
    }

    if (subHeaders && subHeaders.length > 0) {
      return (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button onClick={openTableInNewWindow} className="search-btn">
              크게보기
            </button>
          </div>
          <div className="kb-table-container">
            <table className="kb-table buyer-sentiment-table">
              <thead>
                <tr>
                  <th className="sticky-col" rowSpan="2">날짜</th>
                  {headers.map((region, index) => (
                    <th key={index} colSpan={subHeaders.length}>{region}</th>
                  ))}
                </tr>
                <tr>
                  {headers.map((region, regionIndex) => (
                    subHeaders.map((subHeader, subIndex) => (
                      <th key={`${regionIndex}-${subIndex}`} className="sub-header">
                        {subHeader}
                      </th>
                    ))
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className="sticky-col">{row.baseDate || '-'}</td>
                    {headers.map((region, regionIndex) => (
                      subHeaders.map((subHeader, subIndex) => {
                        const key = subHeader === '매도자많음' ? 'seller'
                          : subHeader === '매수자많음' ? 'buyer'
                            : 'index';
                        const value = row.values?.[key]?.[region];
                        // DESC: 위쪽이 최신 → 전주는 아래쪽 (rowIndex + 1)
                        // ASC: 위쪽이 과거 → 전주는 위쪽 (rowIndex - 1)
                        const prevWeekIndex = dateSortOrder === 'DESC' ? rowIndex + 1 : rowIndex - 1;
                        const prevValue = (prevWeekIndex >= 0 && prevWeekIndex < rows.length)
                          ? rows[prevWeekIndex]?.values?.[key]?.[region]
                          : null;
                        const delta =
                          value !== null && value !== undefined && prevValue !== null && prevValue !== undefined
                            ? Number(value) - Number(prevValue)
                            : null;
                        return (
                          <td key={`${regionIndex}-${subIndex}`} className={getWeeklyChangeClassByDelta(delta)}>
                            {value !== null && value !== undefined ? value.toFixed(2) : '-'}
                          </td>
                        );
                      })
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    const parentRegionSet = new Set(regionHierarchy.map((g) => g.parent));

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          {isWeeklyChangeTable && (
            <div className="kb-change-guide" style={{ marginBottom: 0, flex: 1 }}>
              전 주 데이터 대비 음영 기준: ±0.20%, ±0.40%, ±0.60%, ±0.80% 이상 구간별 상승/하락 표시
            </div>
          )}
          {!isWeeklyChangeTable && <div style={{ flex: 1 }}></div>}
          <button onClick={openTableInNewWindow} className="search-btn" style={{ marginLeft: '12px' }}>
            크게보기
          </button>
        </div>
        <div className="kb-table-container">
          <table className="kb-table kb-table-2row">
            <thead>
              <tr>
                <th className="sticky-col" rowSpan="2">날짜</th>
                {headers.map((region, index) => (
                  <th
                    key={`header1-${index}`}
                    className={parentRegionSet.has(region) ? 'parent-region-header' : 'child-region-header empty-cell'}
                  >
                    {parentRegionSet.has(region) ? region : ''}
                  </th>
                ))}
              </tr>
              <tr>
                {headers.map((region, index) => (
                  <th
                    key={`header2-${index}`}
                    className={parentRegionSet.has(region) ? 'child-region-header empty-cell' : 'child-region-header'}
                  >
                    {parentRegionSet.has(region) ? '' : region}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="sticky-col">{row.baseDate || '-'}</td>
                  {headers.map((region, colIndex) => {
                    const value = row.values?.[region];
                    // DESC: 위쪽이 최신 → 전주는 아래쪽 (rowIndex + 1)
                    // ASC: 위쪽이 과거 → 전주는 위쪽 (rowIndex - 1)
                    const prevWeekIndex = dateSortOrder === 'DESC' ? rowIndex + 1 : rowIndex - 1;
                    const prevValue = (prevWeekIndex >= 0 && prevWeekIndex < rows.length)
                      ? rows[prevWeekIndex]?.values?.[region]
                      : null;
                    const delta =
                      value !== null && value !== undefined && prevValue !== null && prevValue !== undefined
                        ? Number(value) - Number(prevValue)
                        : null;
                    return (
                      <td key={colIndex} className={getWeeklyChangeClassByDelta(delta)}>
                        {value !== null && value !== undefined ? value.toFixed(2) : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="kb-data-page">
      <div className="kb-controls">
        <div className="index-type-selector">
          <select
            value={indexType}
            onChange={(e) => {
              setIndexType(e.target.value);
              setData(null);
            }}
            className="index-select"
          >
            {INDEX_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="date-range">
          <input
            type="date"
            name="startDate"
            value={dateRange.startDate}
            onChange={handleDateChange}
            className="date-input"
          />
          <span>~</span>
          <input
            type="date"
            name="endDate"
            value={dateRange.endDate}
            onChange={handleDateChange}
            className="date-input"
          />
          <button onClick={handleSearch} className="search-btn">
            검색
          </button>
        </div>

        <div className="index-type-selector">
          <select
            value={dateSortOrder}
            onChange={(e) => setDateSortOrder(e.target.value)}
            className="index-select"
          >
            <option value="DESC">날짜 내림차순</option>
            <option value="ASC">날짜 오름차순</option>
          </select>
        </div>

        <div className="kb-region-filter" style={{ marginLeft: '16px', flex: '1' }}>
          <div className="kb-region-filter-head">
            <strong>지역 선택</strong>
            <span>{selectedRegionIds.length}개 선택</span>
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <button onClick={selectAllRegions} className="search-btn" style={{ padding: '4px 12px', fontSize: '13px' }}>
                전체선택
              </button>
              <button onClick={deselectAllRegions} className="search-btn" style={{ padding: '4px 12px', fontSize: '13px' }}>
                전체해제
              </button>
            </div>
          </div>
          <div className="kb-region-check-grid">
          {regionOptions.map((region) => (
            <label key={region.regionId} className="kb-region-check-item">
              <input
                type="checkbox"
                checked={selectedRegionIds.includes(region.regionId)}
                onChange={() => toggleRegion(region.regionId)}
              />
              <span>{region.regionNameKo} ({region.regionId})</span>
            </label>
          ))}
          </div>
        </div>
      </div>

      <div className="kb-content">
        {loading && <div className="loading-indicator">데이터를 불러오는 중...</div>}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && renderTable()}
      </div>
    </div>
  );
};

export default KBDataPage;
